import { IFormContext }          from 'rewire-ui';
import { IRowData }              from 'rewire-grid';
import {
  FieldFilter,
  ITimecardTemplate,
  TimecardTemplateInput,
}                                from '../DataTypes';
import DataMutations             from '../graphql/Mutations';
import DataQueries               from '../graphql/Queries';
import { notifications }         from '../ui/Notifications';
import DocumentDialogModel, {
  DocumentSchema
}                                from './DocumentDialogModel';
import { IEditDialogModelState } from './EditDialogModel';
import mainPageModel             from '../main-navigation/MainPageModel';
import TimecardTemplateGridModel from './TimecardTemplateGridModel';

const TemplateSchema = `
  ${DocumentSchema}
  locked
  template {
    start
    end
    units
    rate
    factor
    amount
    reason {
      id
      name
    }
    overtimeReason {
      id
      name
    }
    accountCode
  }
`;

interface ITemplateDialogModelState extends IEditDialogModelState {
  timecardTemplateGridModel: TimecardTemplateGridModel;
  locked:                    boolean;
}

export class TemplateDialogModel extends DocumentDialogModel<ITemplateDialogModelState> {
  constructor() {
    super(
      'Shift Type',
      (ids: string[]) => DataQueries.getTemplatesByIds(TemplateSchema, ids),
      DataMutations.insertTemplates,
      DataMutations.updateTemplates,
      DataMutations.deleteTemplates,
      'templates',
      () => mainPageModel.canAddTemplates,
      () => mainPageModel.canEditTemplates,
      () => mainPageModel.canDeleteTemplates,
    );

    this.state.timecardTemplateGridModel = new TimecardTemplateGridModel();
    this.state.locked                    = false;
  }

  protected otherFields(form: IFormContext) {
    return {
      color: form.color().label('Color').accessor(['fields', '_color:t']),
    };
  }

  _reset() {
    super._reset({color: '#fff'});
    this.state.timecardTemplateGridModel.reset();
    this.state.locked = false;
  }

  async load(id?: string, isModal: boolean = true) {
    this.isLoading  = true;
    this.id         = id;
    this.isModal    = isModal;
    let template    = undefined;
    if (!this.isNew) {
      const results = await this._load([this.id!]);
      if (!results || (results.length !== 1) || !results[0]) {
        this._reset();
      } else {
        template          = results[0].template;
        this.state.locked = !!results[0].locked;
        this.form.value   = this.mapToForm(results[0]);
      }
    } else {
      this._reset();
    }

    this.state.title = this.getTitle();
    this.state.timecardTemplateGridModel.loadTemplates(template, {headerRowHeight: 28, rowHeight: 34}, this.locked, () => this.viewOnly);
    this.open();
    this.isLoading = false;
  }

  get timecardTemplateGridModel(): TimecardTemplateGridModel {
    return this.state.timecardTemplateGridModel;
  }

  get locked(): boolean {
    return this.state.locked;
  }

  getTimecardTemplateData(): ITimecardTemplate[] {
    return this.timecardTemplateGridModel.get().map((rowData: IRowData) => rowData.data as ITimecardTemplate);
  }

  protected hasChanges(): boolean {
    return this.form.hasChanges || this.timecardTemplateGridModel.hasChanges();
  }

  deleteTemplate = async () => {
    if (!this.canDelete || this.locked) {
      return;
    }
    await this.remove();
  };

  showDuplicateTemplateDialog = () => {
    // load duplicate template dialog
  };

  public mapToSave(): ObjectType {
    const templateInput = super.mapToSave();

    if (!this.locked) {
      const timecardTemplateInputs: TimecardTemplateInput[] = [];

      const timecardTemplatesData = this.getTimecardTemplateData();
      timecardTemplatesData.forEach((timecardTemplateData: ITimecardTemplate) => {
        const timecardTemplateInput: TimecardTemplateInput = {
          units:            timecardTemplateData.units,
          start:            timecardTemplateData.start,
          end:              timecardTemplateData.end,
          rate:             timecardTemplateData.rate,
          factor:           timecardTemplateData.factor,
          amount:           timecardTemplateData.amount,
          reasonId:         timecardTemplateData.reason?.id,
          overtimeReasonId: timecardTemplateData.overtimeReason?.id,
          accountCode:      timecardTemplateData.accountCode,
        };
        timecardTemplateInputs.push(timecardTemplateInput);
      });

      templateInput['template'] = timecardTemplateInputs;
    }

    return templateInput;
  }

  protected async save(): Promise<boolean> {
    if (this.viewOnly || !this.locked && !this.timecardTemplateGridModel.validate()) {
      return false;
    }

    // check to see if a shift type with that name already exists, or should we be just attempting to submit, and letting it fail?
    if (this.isNew) {
      const exists = await this.hasExistingTemplateWithSameName(this.form.field.name.value);
      if (exists) {
        notifications.error('Shift Type Creation Failed: Shift Type with that name already exists');
        return false;
      }
    }

    return await super.save();
  }

  protected async hasExistingTemplateWithSameName(name: string): Promise<boolean> {
    const templateFilter: FieldFilter = {
      field: 'name',
      eq: name,
    };

    const templateSearch = await DataQueries.getSearch('', 'templates', '', templateFilter);
    return !!templateSearch?.length;
  }
}

const templateDialogModel = new TemplateDialogModel();
export default templateDialogModel;
